import React, { useState, useMemo } from 'react';
import { Radio, Button } from '@bedrock/components';
import { priceFormat, XingTuIcon } from '@mpc/components';
import { Mall } from '@bedrock/icons-react';
import classNames from 'classnames/bind';
import styles from './index.less';
import { CellDescriptions } from '@mpc/components';
import { DescriptionsItemType } from '@bedrock/components/lib/Descriptions/Item';
import { CurrencyEnum } from '#services/api';
import MPCProCurrency from '#components/MPCProCurrency';

const cx = classNames.bind(styles);

export interface DataItem {
  type: string;
  value: number;
  currency?: string;
  alias?: string;
}

interface CompositePlatformFieldProps {
  /** 商单平台数据 */
  starMapData?: DataItem[];
  /** 供应商数据 */
  supplierData?: DataItem[];
  /** 主平台报价项目（用于决定显示逻辑） */
  quoteProject?: string;
  /** 视图币种 */
  viewCurrency?: CurrencyEnum;
  mode?: 'amount' | 'text';
  /** 是否展示数据切换按钮 */
  showRadioGroup?: boolean;
}

/**
 * 复合字段组件
 * 根据设计稿需求：
 * 1. 支持商单平台/供应商数据切换
 * 2. 商单平台数据优先显示
 * 3. 根据主平台报价项目决定显示的CPM字段
 *    - 如果主平台报价项目有值，显示对应的CPM，其他收起
 *    - 如果主平台报价项目为空，显示前两个CPM，其他收起
 */
const CompositePlatformField: React.FC<CompositePlatformFieldProps> = ({
  starMapData = [],
  supplierData = [],
  quoteProject,
  viewCurrency,
  mode = 'text',
  showRadioGroup = true,
}) => {
  // 确定使用哪组数据：商单平台数据存在则优先使用商单平台，否则使用供应商
  const hasStarMapData = useMemo(() => starMapData.length > 0, [starMapData]);
  const hasSupplierData = useMemo(
    () => supplierData.length > 0,
    [supplierData]
  );

  // 默认选择商单平台（如果有数据），否则选择供应商
  const [selectedPlatform, setSelectedPlatform] = useState<
    'starMap' | 'supplier'
  >(hasStarMapData ? 'starMap' : 'supplier');

  // 当前使用的数据
  const currentData = useMemo(() => {
    return selectedPlatform === 'starMap' ? starMapData : supplierData;
  }, [selectedPlatform, starMapData, supplierData]);

  // 将数据转换为 DescriptionsItemType 格式
  const descriptionsItems = useMemo<DescriptionsItemType[]>(() => {
    return currentData.map((item) => {
      let children: React.ReactNode;
      if (mode === 'amount') {
        children = (
          <MPCProCurrency
            viewCurrency={viewCurrency}
            fieldProps={{
              value: { currency: item.currency, amount: item.value },
              currencyBeforeAmount: false,
              highlight: false,
            }}
          />
        );
      } else {
        children = item.value?.toLocaleString() ?? '--';
      }

      return {
        label: item.type,
        children,
        key: item.alias,
      };
    });
  }, [currentData, mode, viewCurrency]);

  // 匹配函数：根据 quoteProject 匹配对应的项
  const matchFn = useMemo(() => {
    if (!quoteProject) {
      return undefined;
    }
    return (item: DescriptionsItemType, index: number) => {
      const key = item.key as string;
      const label = item.label as string;
      return (
        (key && quoteProject.toLowerCase().includes(key.toLowerCase())) ||
        (label && quoteProject.toLowerCase().includes(label.toLowerCase()))
      );
    };
  }, [quoteProject]);

  // 如果没有数据，显示 --
  if (currentData.length === 0) {
    return <div>--</div>;
  }

  return (
    <div className={cx('composite-platform-field')}>
      {/* 切换按钮 */}
      {(hasStarMapData || hasSupplierData) && showRadioGroup && (
        <Radio.Group
          value={selectedPlatform}
          onChange={(e) =>
            setSelectedPlatform(e.target.value as 'starMap' | 'supplier')
          }
          className={cx('platform-radio-group')}
        >
          {hasStarMapData && (
            <Radio.Button value="starMap">
              <div className="inline-flex items-center gap-[4px] text-[12px]">
                <XingTuIcon size={14} />
                <span>商单平台</span>
              </div>
            </Radio.Button>
          )}
          {hasSupplierData && (
            <Radio.Button value="supplier">
              <div className="inline-flex items-center gap-[4px] text-[12px]">
                <Mall size={14} />
                <span>供应商</span>
              </div>
            </Radio.Button>
          )}
        </Radio.Group>
      )}

      {/* 内容区域 */}
      <div className={cx('content')}>
        <CellDescriptions
          column={1}
          items={descriptionsItems}
          maxDisplayCount={2}
          matchFn={matchFn}
          otherLabel="其他"
          popoverProps={{
            popupContentProps: {
              className: 'rock-popover-inner max-w-[480px]',
            },
          }}
        />
      </div>
    </div>
  );
};

export default CompositePlatformField;
